import React, { useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import logoImage from "../assets/logo.png";
import loginImage from "../assets/loginimage.png";

const AUTH_FONT_FAMILY = '"Inter", "Segoe UI", Arial, sans-serif';
const BRAND_GRADIENT = "linear-gradient(90deg, #22C55E 0%, #2563EB 100%)";
const DESKTOP_CURVE_WIDTH = 296;
const DESKTOP_CURVE_OFFSET = 176;
const DESKTOP_PANEL_RADIUS = "0 100% 100% 0 / 0 46% 46% 0";
const DESKTOP_CURVE_VERTICAL_BLEED = 120;
const FEATURE_ITEMS = [
  { icon: ShieldOutlinedIcon, title: "Secure & Reliable" },
  { icon: AccessTimeOutlinedIcon, title: "Real-time Tracking" },
  { icon: QueryStatsOutlinedIcon, title: "Advanced Reports" },
  { icon: FmdGoodOutlinedIcon, title: "Geo Fencing" },
];

function Signup() {
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const mobileHint = useMemo(() => {
    if (!mobileNo) return "Optional";
    const ok = /^[0-9+\-\s()]{7,}$/.test(mobileNo);
    return ok ? "Looks valid" : "Check mobile format";
  }, [mobileNo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    setShowForm(false);
    setIsLoading(true);

    try {
      await axios.post(`${backendUrl}/auth/register`, {
        username,
        email,
        mobileNo,
        password,
      });

      setMessage("Signup successful! Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      console.error("Signup error:", err.response ? err.response.data : err.message);
      const errorMessage = err.response
        ? err.response.data.msg || "Signup failed."
        : "Network error.";
      setError(errorMessage);

      setTimeout(() => {
        setShowForm(true);
        setError("");
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: { lg: "100vh" },
        overflow: "hidden",
        bgcolor: "#f6f8fb",
        fontFamily: AUTH_FONT_FAMILY,
      }}
    >
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          height: { lg: "100vh" },
          flexDirection: { xs: "column", md: "column", lg: "row" },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: { xs: "100%", lg: "45%" },
            minHeight: { xs: "100vh", lg: "100vh" },
            bgcolor: "#ffffff",
            display: "flex",
            justifyContent: "center",
            overflow: { xs: "hidden", lg: "visible" },
            boxShadow: { lg: "28px 0 60px rgba(15, 23, 42, 0.06)" },
            "&::before": {
              content: '""',
              display: { xs: "none", lg: "block" },
              position: "absolute",
              top: `-${DESKTOP_CURVE_VERTICAL_BLEED / 2}px`,
              right: `-${DESKTOP_CURVE_OFFSET + 6}px`,
              width: `${DESKTOP_CURVE_WIDTH + 12}px`,
              height: `calc(100% + ${DESKTOP_CURVE_VERTICAL_BLEED}px)`,
              background: "transparent",
              borderRight: "6px solid rgba(34, 197, 94, 0.96)",
              borderRadius: DESKTOP_PANEL_RADIUS,
              zIndex: 1,
            },
            "&::after": {
              content: '""',
              display: { xs: "none", lg: "block" },
              position: "absolute",
              top: `-${DESKTOP_CURVE_VERTICAL_BLEED / 2}px`,
              right: `-${DESKTOP_CURVE_OFFSET}px`,
              width: `${DESKTOP_CURVE_WIDTH}px`,
              height: `calc(100% + ${DESKTOP_CURVE_VERTICAL_BLEED}px)`,
              background: "#ffffff",
              borderRadius: DESKTOP_PANEL_RADIUS,
              zIndex: 2,
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              width: "100%",
              maxWidth: 620,
              px: { xs: 3, sm: 5, lg: 6 },
              py: { xs: 4, sm: 5, lg: 7 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: { xs: 3, lg: 4 },
            }}
          >
            <Stack spacing={{ xs: 4, lg: 5 }}>
              <Stack direction="row" spacing={2.2} alignItems="center">
                <Box
                  component="img"
                  src={logoImage}
                  alt="GPS Controller"
                  sx={{
                    width: { xs: 74, sm: 88, lg: 108 },
                    height: { xs: 74, sm: 88, lg: 108 },
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />
                <Stack spacing={0.3}>
                  <Typography
                    sx={{
                      fontSize: { xs: 22, sm: 28, lg: 34 },
                      lineHeight: 1,
                      fontWeight: 800,
                      letterSpacing: "-0.04em",
                      color: "#0F2740",
                    }}
                  >
                    GPS
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 20, sm: 25, lg: 33 },
                      lineHeight: 1,
                      fontWeight: 700,
                      letterSpacing: "-0.03em",
                      color: "#20B374",
                    }}
                  >
                    CONTROLLER
                  </Typography>
                  <Typography
                    sx={{
                      pt: 1,
                      color: "#667085",
                      fontSize: { xs: 14, sm: 15, lg: 16 },
                      fontWeight: 500,
                    }}
                  >
                    Track. Monitor. Protect.
                  </Typography>
                </Stack>
              </Stack>

              <Stack spacing={1.4}>
                <Typography
                  sx={{
                    fontSize: { xs: 38, sm: 48, lg: 56 },
                    lineHeight: 1.04,
                    fontWeight: 800,
                    letterSpacing: "-0.05em",
                    color: "#0B2239",
                  }}
                >
                  Create{" "}
                  <Box
                    component="span"
                    sx={{
                      backgroundImage: BRAND_GRADIENT,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Account!
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    color: "#667085",
                    fontSize: { xs: 17, lg: 18 },
                    fontWeight: 500,
                  }}
                >
                  Signup to access your GPS tracking dashboard
                </Typography>
              </Stack>

              <Stack spacing={2.3}>
                {message && <Alert severity="success">{message}</Alert>}
                {error && <Alert severity="error">{error}</Alert>}

                {showForm && (
                  <Stack component="form" onSubmit={handleSubmit} noValidate spacing={2.1}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <Stack spacing={1} sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                          Username
                        </Typography>
                        <TextField
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          fullWidth
                          required
                          autoComplete="username"
                          placeholder="Enter username"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonOutlineOutlinedIcon sx={{ color: "#475467", fontSize: 24 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: 58,
                              borderRadius: "14px",
                              bgcolor: "#fff",
                              fontSize: 16,
                              boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
                              "& fieldset": { borderColor: "#E5E7EB" },
                              "&:hover fieldset": { borderColor: "#CBD5E1" },
                              "&.Mui-focused fieldset": { borderColor: "#22C55E", borderWidth: 1 },
                            },
                          }}
                        />
                      </Stack>

                      <Stack spacing={1} sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                          Mobile Number
                        </Typography>
                        <TextField
                          value={mobileNo}
                          onChange={(e) => setMobileNo(e.target.value)}
                          fullWidth
                          placeholder="Enter mobile number"
                          helperText={mobileHint}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIphoneOutlinedIcon sx={{ color: "#475467", fontSize: 24 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: 58,
                              borderRadius: "14px",
                              bgcolor: "#fff",
                              fontSize: 16,
                              boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
                              "& fieldset": { borderColor: "#E5E7EB" },
                              "&:hover fieldset": { borderColor: "#CBD5E1" },
                              "&.Mui-focused fieldset": { borderColor: "#22C55E", borderWidth: 1 },
                            },
                            "& .MuiFormHelperText-root": {
                              mx: 0,
                              mt: 0.8,
                            },
                          }}
                        />
                      </Stack>
                    </Stack>

                    <Stack spacing={1}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                        Email
                      </Typography>
                      <TextField
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        required
                        autoComplete="email"
                        placeholder="Enter email address"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon sx={{ color: "#475467", fontSize: 24 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 58,
                            borderRadius: "14px",
                            bgcolor: "#fff",
                            fontSize: 16,
                            boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
                            "& fieldset": { borderColor: "#E5E7EB" },
                            "&:hover fieldset": { borderColor: "#CBD5E1" },
                            "&.Mui-focused fieldset": { borderColor: "#22C55E", borderWidth: 1 },
                          },
                        }}
                      />
                    </Stack>

                    <Stack spacing={1}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                        Password
                      </Typography>
                      <TextField
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        required
                        autoComplete="new-password"
                        placeholder="Enter your password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon sx={{ color: "#475467", fontSize: 24 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                edge="end"
                                onClick={() => setShowPassword((s) => !s)}
                              >
                                {showPassword ? (
                                  <VisibilityOffOutlinedIcon sx={{ color: "#475467" }} />
                                ) : (
                                  <VisibilityOutlinedIcon sx={{ color: "#475467" }} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 58,
                            borderRadius: "14px",
                            bgcolor: "#fff",
                            fontSize: 16,
                            boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
                            "& fieldset": { borderColor: "#E5E7EB" },
                            "&:hover fieldset": { borderColor: "#CBD5E1" },
                            "&.Mui-focused fieldset": { borderColor: "#22C55E", borderWidth: 1 },
                          },
                        }}
                      />
                    </Stack>

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading}
                      startIcon={!isLoading ? <HowToRegOutlinedIcon sx={{ fontSize: 22 }} /> : null}
                      sx={{
                        mt: 0.8,
                        height: 60,
                        borderRadius: "14px",
                        textTransform: "none",
                        fontFamily: AUTH_FONT_FAMILY,
                        fontSize: 18,
                        fontWeight: 700,
                        color: "#fff",
                        background: BRAND_GRADIENT,
                        boxShadow: "0 18px 30px rgba(37, 99, 235, 0.20)",
                        "&:hover": {
                          background: BRAND_GRADIENT,
                          boxShadow: "0 24px 40px rgba(37, 99, 235, 0.28)",
                        },
                        "&.Mui-disabled": {
                          color: "rgba(255,255,255,0.78)",
                          background: BRAND_GRADIENT,
                        },
                      }}
                    >
                      {isLoading ? (
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <CircularProgress size={18} color="inherit" />
                          <span>Creating account...</span>
                        </Stack>
                      ) : (
                        "Signup"
                      )}
                    </Button>
                  </Stack>
                )}

                <Typography
                  sx={{
                    pt: 0.4,
                    color: "#667085",
                    fontSize: 15,
                    fontWeight: 500,
                    textAlign: { xs: "center", sm: "left" },
                  }}
                >
                  Already have an account?{" "}
                  <Link
                    component={RouterLink}
                    to="/login"
                    underline="none"
                    sx={{
                      color: "#2563EB",
                      fontWeight: 700,
                    }}
                  >
                    Login here
                  </Link>
                </Typography>
              </Stack>
            </Stack>

            <Stack spacing={2.5}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: { xs: 1.5, lg: 0 },
                  pt: { xs: 0.5, lg: 1.5 },
                }}
              >
                {FEATURE_ITEMS.map(({ icon: Icon, title }, index) => (
                  <Stack
                    key={title}
                    alignItems="center"
                    spacing={1.3}
                    sx={{
                      textAlign: "center",
                      px: { xs: 1, lg: 1.4 },
                      py: 0.5,
                      borderRight: {
                        lg: index === FEATURE_ITEMS.length - 1 ? "none" : "1px solid #E4E7EC",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        color: "#20B374",
                        bgcolor: alpha("#22C55E", 0.08),
                      }}
                    >
                      <Icon sx={{ fontSize: 26 }} />
                    </Box>
                    <Typography
                      sx={{
                        color: "#101828",
                        fontSize: { xs: 15, lg: 16 },
                        fontWeight: 600,
                        lineHeight: 1.35,
                      }}
                    >
                      {title}
                    </Typography>
                  </Stack>
                ))}
              </Box>

              <Typography
                sx={{
                  color: "#667085",
                  fontSize: 14,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", lg: "flex-start" },
                  gap: 1,
                  pt: { xs: 0, lg: 1 },
                }}
              >
                <ShieldOutlinedIcon sx={{ color: "#16A34A", fontSize: 18 }} />
                © 2026 GPS Controller. All rights reserved.
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: "none", lg: "flex" },
            width: "55%",
            minHeight: "100vh",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#f8fafc",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={loginImage}
            alt="GPS tracking visual"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "center center",
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default Signup;
